<template>
  <UTextarea
    ref="textarea"
    v-bind="{ ...forwardedProps, ...$attrs }"
    :loading="false"
    :model-value="innerModelValue"
    class="p-textarea"
    :aria-busy="props.loading || undefined"
    @update:model-value="onModelUpdate"
    @focusin="hasFocus = true"
    @focusout="hasFocus = false"
    @blur="emit('blur', $event)"
    @change="emit('change', $event)"
  >
    <template v-if="slots.leading || leadingIcon" #leading="slotProps">
      <slot name="leading" v-bind="slotProps">
        <FontAwesomeIcon
          v-if="leadingIcon"
          :icon="leadingIcon"
          aria-hidden="true"
          data-slot="leadingIcon"
          :class="[
            slotProps.ui.leadingIcon({ class: props.ui?.leadingIcon }),
            { 'animate-spin': loading }
          ]"
        />
      </slot>
    </template>
    <template v-if="slots.default" #default="slotProps"><slot v-bind="slotProps" /></template>
    <template v-if="slots.trailing || trailingIcon" #trailing="slotProps">
      <slot name="trailing" v-bind="slotProps">
        <FontAwesomeIcon
          v-if="trailingIcon"
          :icon="trailingIcon"
          aria-hidden="true"
          data-slot="trailingIcon"
          :class="[
            slotProps.ui.trailingIcon({ class: props.ui?.trailingIcon, leading: isLeading }),
            { 'animate-spin': loading && !isLeading }
          ]"
        />
      </slot>
    </template>
  </UTextarea>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watchEffect } from "vue"
import NuxtTextarea from "@nuxt/ui/components/Textarea.vue"
import type { PTextareaProps, PTextareaSlots } from "./textarea"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { reactiveOmit } from "@vueuse/core"

const UTextarea = NuxtTextarea<string | null>

const props = withDefaults(defineProps<PTextareaProps>(), {
  color: "secondary",
  variant: "outline",
  size: "md",
  leading: undefined,
  trailing: undefined
})
const emit = defineEmits<{
  "update:modelValue": [value: string | null | undefined]
  blur: [event: FocusEvent]
  change: [event: Event]
}>()
const slots = defineSlots<PTextareaSlots>()
const forwardedProps = reactiveOmit(
  props,
  "icon",
  "leadingIcon",
  "trailingIcon",
  "loadingIcon",
  "loading"
)
const isLeading = computed(() =>
  Boolean(
    props.leadingIcon ||
    (props.icon && (props.leading || !props.trailing)) ||
    (props.loading && !props.trailing)
  )
)
const leadingIcon = computed(() =>
  isLeading.value
    ? ((props.loading
        ? props.loadingIcon || faSpinner
        : props.leadingIcon || props.icon) as IconProp)
    : undefined
)
const trailingIcon = computed(() =>
  (props.icon && props.trailing) ||
  (props.loading && props.trailing && !isLeading.value) ||
  (props.trailingIcon && props.trailing !== false)
    ? ((props.loading && !isLeading.value
        ? props.loadingIcon || faSpinner
        : props.trailingIcon || props.icon) as IconProp)
    : undefined
)
const hasFocus = ref(false)
const innerModelValue = ref(props.modelValue)

watchEffect(() => {
  // Save responses and live updates must not replace text or move the caret during editing.
  if (!props.ignoreUpdatesWhileFocused || !hasFocus.value) {
    innerModelValue.value = props.modelValue
  }
})

function onModelUpdate(value: string | null | undefined) {
  innerModelValue.value = value
  emit("update:modelValue", value)
}

const textarea = useTemplateRef<{
  textareaRef: HTMLTextAreaElement | null
  autoResize: () => void
}>("textarea")
defineOptions({ inheritAttrs: false })
defineExpose({
  get textareaRef() {
    return textarea.value?.textareaRef
  },
  focus: () => textarea.value?.textareaRef?.focus(),
  blur: () => textarea.value?.textareaRef?.blur(),
  autoResize: () => textarea.value?.autoResize()
})
</script>
